/**
 * assessment-report.component.ts
 * --------------------------------
 * Module 11 — Assessment Report Page.
 *
 * Layout (matches image design):
 *  Left panel  → Report info card + Guardrails Summary score boxes
 *  Right panel → Analysis Assistant (echoes prompt for now)
 *  Bottom bar  → Start New Assessment | Download Report | Back to Category Selection
 *
 * Clicking a summary box opens a modal showing all guardrails for that status.
 * The modal supports Edit mode (status, justification, artifacts editable).
 * Download Report triggers DOCX download + saves JSON to blob storage.
 */
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewChecked,
  ElementRef,
  ViewChild,
  signal,
  computed,
} from '@angular/core';
import { CommonModule }      from '@angular/common';
import { FormsModule }       from '@angular/forms';
import { Router }            from '@angular/router';

import { SessionService }    from '../../core/services/session.service';
import {
  AssessmentService,
  AssessmentResultsResponse,
  GuardrailAssessmentEntry,
} from '../../core/services/assessment.service';
import { ChatService, ChatHistoryMessage } from '../../core/services/chat.service';
import { MarkdownPipe }      from '../../shared/pipes/markdown.pipe';

/** How often (ms) to poll the status endpoint. */
const POLL_INTERVAL_MS = 2500;

type PageState = 'idle' | 'loading' | 'ready' | 'error';

const STATUS_CONFIG: Record<string, { label: string; css: string; icon: string }> = {
  'Met':             { label: 'Met',           css: 'status--met',     icon: '✓' },
  'Partially Met':   { label: 'Partially Met', css: 'status--partial', icon: '◑' },
  'Not Met':         { label: 'Not Met',        css: 'status--not-met', icon: '✗' },
  'Not Applicable':  { label: 'Not Applicable', css: 'status--na',      icon: '—' },
};

export interface EditableEntry extends GuardrailAssessmentEntry {
  _edited?: boolean;
}

@Component({
  selector:    'app-assessment-report',
  standalone:  true,
  imports:     [CommonModule, FormsModule, MarkdownPipe],
  templateUrl: './assessment-report.component.html',
  styleUrl:    './assessment-report.component.scss',
})
export class AssessmentReportComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('chatScroll') private _chatScroll!: ElementRef<HTMLDivElement>;
  private _shouldScrollToBottom = false;

  // ── Page state ──────────────────────────────────────────────────────────
  pageState  = signal<PageState>('idle');
  errorMsg   = signal<string>('');
  progress   = signal<number>(0);
  statusMsg  = signal<string>('Initialising…');

  readonly loaderPhases = [
    { key: 'retrieving',  label: 'Guardrail Retrieval',      icon: '📚' },
    { key: 'assessing',   label: 'AI Guardrail Assessment',  icon: '🤖' },
    { key: 'compiling',   label: 'Compiling Report',         icon: '📊' },
  ] as const;

  activePhaseKey = computed<string>(() => {
    const p = this.progress();
    if (p < 50) return 'retrieving';
    if (p < 90) return 'assessing';
    return 'compiling';
  });

  isPhaseComplete = (key: string): boolean => {
    const p = this.progress();
    if (key === 'retrieving') return p >= 50;
    if (key === 'assessing')  return p >= 90;
    if (key === 'compiling')  return p >= 100;
    return false;
  };

  // ── Session data ─────────────────────────────────────────────────────────
  finalizedCategories: string[] = [];
  sessionId: string | null      = null;

  // ── Results ─────────────────────────────────────────────────────────────
  results     = signal<AssessmentResultsResponse | null>(null);
  assessments = signal<EditableEntry[]>([]);

  // ── Computed summary counts (live from mutable assessments array) ────────
  metCount     = computed(() => this.assessments().filter(a => a.status === 'Met').length);
  partialCount = computed(() => this.assessments().filter(a => a.status === 'Partially Met').length);
  notMetCount  = computed(() => this.assessments().filter(a => a.status === 'Not Met').length);
  naCount      = computed(() => this.assessments().filter(a => a.status === 'Not Applicable').length);
  totalCount   = computed(() => this.metCount() + this.partialCount() + this.notMetCount() + this.naCount());

  // ── Application metadata helpers ─────────────────────────────────────────
  appDetails = computed<Record<string, any>>(() =>
    this.results()?.application_details ?? {}
  );

  appName = computed<string>(() => {
    const d = this.appDetails();
    return d['appName'] || d['app_name'] || 'Application';
  });

  reportFileName = computed<string>(() => {
    const name = this.appName().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
    return `${name}_Guardrail_Analysis.docx`;
  });

  generatedDate = computed<string>(() => {
    const iso = this.results()?.generated_at;
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch { return iso; }
  });

  // ── Modal ────────────────────────────────────────────────────────────────
  modalOpen      = signal<boolean>(false);
  modalFilter    = signal<string>('all');
  modalTitle     = signal<string>('All Guardrails');
  modalCatFilter = signal<string | null>(null);

  /** Unique categories in the currently filtered modal rows (before cat filter). */
  modalCategories = computed<string[]>(() => {
    const all = this.assessments();
    const f   = this.modalFilter();
    const rows = f === 'all' ? all : all.filter(a => a.status === f);
    return Array.from(new Set(rows.map(r => r.category)));
  });

  /** Final visible rows: status-filtered AND category-filtered. */
  modalRows = computed<EditableEntry[]>(() => {
    const all = this.assessments();
    const f   = this.modalFilter();
    const cat = this.modalCatFilter();
    let rows  = f === 'all' ? all : all.filter(a => a.status === f);
    if (cat) rows = rows.filter(r => r.category === cat);
    return rows;
  });

  /** guardrail_id of the entry currently in edit mode ('' = none). */
  editingId  = signal<string>('');
  editDraft: {
    status: string;
    justification: string;
    confidence_score: number;
    artifacts_text: string;
  } = {
    status: '', justification: '', confidence_score: 0, artifacts_text: '',
  };

  readonly statusOptions = ['Met', 'Partially Met', 'Not Met', 'Not Applicable'];

  // ── Chat panel ────────────────────────────────────────────────────────────
  chatMessages = signal<{ role: 'user' | 'assistant'; text: string }[]>([
    {
      role: 'assistant',
      text: "Welcome to the Analysis Assistant! I'm here to help you understand your guardrails analysis report. You can ask me questions about:\n• Specific findings in your report\n• Details about uploaded documents\n• Guardrail requirements and compliance\n• Recommendations for improvement\n\nWhat would you like to know?",
    },
  ]);
  chatInput   = signal<string>('');
  chatLoading = signal<boolean>(false);

  // ── Download state ────────────────────────────────────────────────────────
  downloadingReport = signal<boolean>(false);

  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  readonly statusConfig = STATUS_CONFIG;

  constructor(
    private readonly sessionSvc:    SessionService,
    private readonly assessmentSvc: AssessmentService,
    private readonly chatSvc:       ChatService,
    private readonly router:        Router,
  ) {}

  ngOnInit(): void {
    this.finalizedCategories = this.sessionSvc.getFinalizedCategories();
    this.sessionId           = this.sessionSvc.sessionId();
    this.pageState.set('loading');
    if (this.sessionId) {
      this._tryRestoreOrAttach();
    }
  }

  ngOnDestroy(): void {
    this._stopPolling();
    document.body.style.overflow = '';
  }

  ngAfterViewChecked(): void {
    if (this._shouldScrollToBottom) {
      this._shouldScrollToBottom = false;
      const el = this._chatScroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  runAssessment(): void {
    if (!this.sessionId || !this.finalizedCategories.length) { return; }
    this.pageState.set('loading');
    this.progress.set(0);
    this.statusMsg.set('Starting assessment pipeline…');
    this.errorMsg.set('');
    this.assessmentSvc
      .startAssessment(this.sessionId, this.finalizedCategories)
      .subscribe({
        next:  () => this._startPolling(),
        error: (err: Error) => this._handleError(err.message),
      });
  }

  rerunAssessment(): void {
    if (!this.sessionId) { return; }
    this._stopPolling();
    this.results.set(null);
    this.assessments.set([]);
    this.editingId.set('');
    this.assessmentSvc.reset(this.sessionId).subscribe({
      next:  () => this.runAssessment(),
      error: () => this.runAssessment(),
    });
  }

  goBack(): void {
    this.router.navigate(['/category-review']);
  }

  startNewAssessment(): void {
    // Mirror the browser "Leave site?" warning shown on refresh.
    // Remind the user to download the report before losing their data.
    const confirmed = window.confirm(
      '⚠️  Start a New Assessment?\n\n' +
      'All current assessment data for this session will be permanently deleted.\n\n' +
      'Please make sure you have downloaded your report before continuing.\n\n' +
      'Click OK to start a new assessment, or Cancel to return to the report.',
    );
    if (!confirmed) { return; }

    // Delete the backend session (blob storage) then reset local state,
    // then navigate to the landing page — same cleanup as inactivity timeout.
    this.sessionSvc.deleteAndReset().subscribe(() => {
      this.router.navigate(['/']);
    });
  }

  // ── Summary box → modal ───────────────────────────────────────────────────

  openModal(filter: string, title: string): void {
    this.editingId.set('');
    this.modalFilter.set(filter);
    this.modalCatFilter.set(null);
    this.modalTitle.set(title);
    this.modalOpen.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeModal(): void {
    this.editingId.set('');
    this.modalOpen.set(false);
    document.body.style.overflow = '';
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────

  startEdit(row: EditableEntry): void {
    // If the analyst has already edited artifacts_text, restore their edits.
    // Otherwise pre-populate from the structured proof[] list so they can see
    // (and tweak) the original AI-generated evidence rather than a blank box.
    let initialArtifactsText = row.artifacts_text ?? '';
    if (!initialArtifactsText && row.proof?.length) {
      initialArtifactsText = row.proof
        .map(p => `${p.document_name} (p.${p.page_number}): ${p.referenced_text}`)
        .join('\n');
    }

    this.editDraft = {
      status:           row.status,
      justification:    row.justification,
      confidence_score: row.confidence_score,
      artifacts_text:   initialArtifactsText,
    };
    this.editingId.set(row.guardrail_id);
  }

  cancelEdit(): void {
    this.editingId.set('');
  }

  saveEdit(guardrailId: string): void {
    this.assessments.update(list =>
      list.map(a =>
        a.guardrail_id === guardrailId
          ? { ...a, ...this.editDraft, _edited: true }
          : a
      )
    );
    this.editingId.set('');
  }

  isEditing(row: EditableEntry): boolean {
    return this.editingId() === row.guardrail_id;
  }

  // ── Download & save ───────────────────────────────────────────────────────

  downloadReport(): void {
    if (!this.sessionId || this.downloadingReport()) { return; }

    const acknowledged = window.confirm(
      'By downloading this report, you confirm that you have reviewed the AI-generated assessment results and acknowledge that the content may be incorrect or incomplete. Human review and validation of all results is strongly recommended before use in any official capacity.\n\nDo you wish to proceed with the download?'
    );
    if (!acknowledged) { return; }

    this.downloadingReport.set(true);

    // Build updated results payload with edited assessments
    const updatedResults: AssessmentResultsResponse = {
      ...this.results()!,
      assessments: this.assessments(),
      summary: {
        total_guardrails: this.assessments().length,
        met:              this.metCount(),
        partially_met:    this.partialCount(),
        not_met:          this.notMetCount(),
        not_applicable:   this.naCount(),
      },
    };

    // Stamp acknowledgment — user just confirmed the disclaimer dialog
    const savePayload: any = {
      ...updatedResults,
      acknowledged: true,
    };

    // Save JSON to blob first (with acknowledgment stamped), then trigger DOCX download
    this.assessmentSvc.saveReport(this.sessionId, savePayload).subscribe({
      next:  () => this._triggerDocxDownload(),
      error: () => this._triggerDocxDownload(), // still try download on save failure
    });
  }

  private _triggerDocxDownload(): void {
    if (!this.sessionId) { this.downloadingReport.set(false); return; }
    this.assessmentSvc.downloadReport(this.sessionId).subscribe({
      next: (blob: Blob) => {
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = this.reportFileName();
        a.click();
        URL.revokeObjectURL(url);
        this.downloadingReport.set(false);
      },
      error: () => this.downloadingReport.set(false),
    });
  }

  // ── Chat ──────────────────────────────────────────────────────────────────

  sendChat(): void {
    const msg = this.chatInput().trim();
    if (!msg || this.chatLoading() || !this.sessionId) return;

    // Append user message and seed an empty assistant bubble to stream into
    this.chatMessages.update(m => [
      ...m,
      { role: 'user',      text: msg },
      { role: 'assistant', text: '' },
    ]);
    this._shouldScrollToBottom = true;
    this.chatInput.set('');
    this.chatLoading.set(true);

    // History = all messages except the welcome bubble and the two just added
    const allMsgs = this.chatMessages();
    const history: ChatHistoryMessage[] = allMsgs
      .slice(1, -2)
      .map(m => ({ role: m.role, text: m.text }));

    this.chatSvc.streamMessage(this.sessionId, msg, history).subscribe({
      next: (chunk: string) => {
        // Append each delta to the last (assistant) bubble in-place
        this.chatMessages.update(msgs => {
          const copy = [...msgs];
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            text: copy[copy.length - 1].text + chunk,
          };
          return copy;
        });
        this._shouldScrollToBottom = true;
      },
      error: (err: Error) => {
        this.chatMessages.update(msgs => {
          const copy = [...msgs];
          copy[copy.length - 1] = {
            role: 'assistant',
            text: `⚠️ ${err.message || 'The assistant is temporarily unavailable. Please try again.'}`,
          };
          return copy;
        });
        this.chatLoading.set(false);
      },
      complete: () => {
        this.chatLoading.set(false);
      },
    });
  }

  onChatKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChat();
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getStatusCss(status: string): string  { return STATUS_CONFIG[status]?.css   ?? 'status--na'; }
  getStatusIcon(status: string): string { return STATUS_CONFIG[status]?.icon  ?? '—'; }
  getStatusLabel(status: string): string{ return STATUS_CONFIG[status]?.label ?? status; }

  truncate(text: string, maxLen = 110): string {
    return text && text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
  }

  isLlmError(justification: string): boolean {
    return justification?.startsWith('There may have been an error communicating with the AI service');
  }

  trackById(_: number, row: EditableEntry): string {
    return row.guardrail_id;
  }

  getAppFieldDisplay(key: string): string {
    const d = this.appDetails();
    const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    return d[key] ?? d[camel] ?? '—';
  }

  // ── Private pipeline helpers ─────────────────────────────────────────────

  private _tryRestoreOrAttach(): void {
    this.assessmentSvc.getStatus(this.sessionId!).subscribe({
      next: (s) => {
        if (s.status === 'processing') {
          this.pageState.set('loading');
          this.progress.set(s.progress);
          this.statusMsg.set(s.message || 'Assessment in progress…');
          this._startPolling();
        } else if (s.status === 'completed') {
          this._fetchResults();
        } else {
          this.runAssessment();
        }
      },
      error: () => this.runAssessment(),
    });
  }

  private _startPolling(): void {
    this._stopPolling();
    this._pollTimer = setInterval(() => this._pollStatus(), POLL_INTERVAL_MS);
    this._pollStatus();
  }

  private _stopPolling(): void {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _pollStatus(): void {
    if (!this.sessionId) { this._stopPolling(); return; }
    this.assessmentSvc.getStatus(this.sessionId).subscribe({
      next: (s) => {
        this.progress.set(s.progress);
        this.statusMsg.set(s.message);
        if (s.status === 'completed') {
          this._stopPolling();
          this._fetchResults();
        } else if (s.status === 'failed') {
          this._stopPolling();
          this._handleError(s.error ?? 'Assessment pipeline failed.');
        }
      },
      error: (err: Error) => console.warn('[AssessmentReport] Poll error:', err.message),
    });
  }

  private _fetchResults(): void {
    if (!this.sessionId) { return; }
    this.assessmentSvc.getResults(this.sessionId).subscribe({
      next: (res) => {
        this.results.set(res);
        this.assessments.set([...(res.assessments ?? [])] as EditableEntry[]);
        this.pageState.set('ready');
      },
      error: (err: Error) => this._handleError(err.message),
    });
  }

  private _handleError(msg: string): void {
    this.errorMsg.set(msg || 'An unexpected error occurred.');
    this.pageState.set('error');
  }
}
